class GemiHeroSlider {
  constructor(el) {
    this.el = el;
    this.track = el.querySelector('.gemi-hero-slider__track');
    this.slides = Array.from(el.querySelectorAll('.gemi-hero-slider__slide'));
    this.prevBtn = el.querySelector('.gemi-hero-slider__arrow--prev');
    this.nextBtn = el.querySelector('.gemi-hero-slider__arrow--next');
    this.dotsWrap = el.querySelector('.gemi-hero-slider__dots');

    this.total = this.slides.length;
    if (this.total <= 1) {
      if (this.prevBtn) this.prevBtn.hidden = true;
      if (this.nextBtn) this.nextBtn.hidden = true;
      if (this.dotsWrap) this.dotsWrap.hidden = true;

      this.index = 0;
      this._observeHeroVisibility();
      this._updateVideoHeaderClass();
      return;
    }

    this.infinite = el.dataset.infinite === 'true';
    this.delay = (parseInt(el.dataset.autoplayDelay, 10) || 0) * 1000;
    this.pauseHover = el.dataset.pauseOnHover === 'true';
    this.direction = el.dataset.scrollDirection || 'forward';

    this.index = 0; // real slide index (0, 1, 2 …)
    this.moving = false;
    this.timer = null;
    this.cloneOffset = 0;

    this._observeHeroVisibility();

    if (this.infinite) this._addClones();

    this._buildDots();
    this._bindEvents();

    // Start on the first real slide
    if (this.infinite) {
      this._setPosition(this.cloneOffset, false);
    } else {
      this._setPosition(0, false);
    }
    this._updateDots();
    this._playActiveVideo();

    if (this.delay) this._play();
  }

  /* ---------- Clones ---------- */
  _addClones() {
    const first = this.slides[0].cloneNode(true);
    const last = this.slides[this.total - 1].cloneNode(true);

    [first, last].forEach(node => {
      node.classList.add('gemi-hero-slider__slide--clone');
      node.removeAttribute('id');
      node.setAttribute('aria-hidden', 'true');
      node.querySelectorAll('img').forEach(img => {
        img.loading = 'lazy';
        img.removeAttribute('fetchpriority');
      });
      // Pause any cloned videos so they don't stream in the background
      node.querySelectorAll('video').forEach(vid => {
        vid.pause();
        vid.removeAttribute('autoplay');
        vid.muted = true;
      });
    });

    this.track.appendChild(first);
    this.track.insertBefore(last, this.slides[0]);
    this.cloneOffset = 1;
  }

  /* ---------- Dots ---------- */
  _buildDots() {
    if (!this.dotsWrap) return;
    this.dotsWrap.innerHTML = '';
    for (let i = 0; i < this.total; i++) {
      const btn = document.createElement('button');
      btn.className = 'gemi-hero-slider__dot';
      btn.dataset.slide = i;
      btn.setAttribute('aria-label', 'Slide ' + (i + 1));
      this.dotsWrap.appendChild(btn);
    }
    this.dots = Array.from(this.dotsWrap.children);
  }

  _updateDots() {
    if (!this.dots) return;
    this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === this.index));
  }

  /* ---------- Video ---------- */
  _playActiveVideo() {
    this.slides.forEach((slide, i) => {
      const video = slide.querySelector('video');
      if (!video) return;
      if (i === this.index) {
        video.muted = true;
        video.play().catch(() => {});
      } else {
        video.pause();
        video.currentTime = 0;
      }
    });
    this._updateVideoHeaderClass();
  }

  _pauseAllVideos() {
    this.slides.forEach(slide => {
      const video = slide.querySelector('video');
      if (video) video.pause();
    });
  }

  /* ---------- Header color (white text over active video slide) ---------- */
  _observeHeroVisibility() {
    this._heroInView = true;
    if (!('IntersectionObserver' in window)) return;

    const headerHeight = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue('--header-height')
    ) || 0;

    this._io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        this._heroInView = entry.isIntersecting;
        this._updateVideoHeaderClass();
      });
    }, { rootMargin: `-${headerHeight}px 0px 0px 0px`, threshold: 0 });

    this._io.observe(this.el);
  }

  _updateVideoHeaderClass() {
  const activeSlide = this.slides[this.index];
  const hasVideo = !!(activeSlide && activeSlide.querySelector('video'));
  document.body.classList.toggle('gemi-hero-video-active', hasVideo);
}

  /* ---------- Position ---------- */
  _setPosition(trackIndex, animate) {
    this.track.style.transition = animate
      ? 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
      : 'none';
    this.track.style.transform = 'translateX(-' + (trackIndex * 100) + '%)';

    if (!animate) {
      void this.track.offsetWidth; // force browser to paint the instant jump
    }
  }

  _afterTransition(cb) {
    let called = false;
    const done = () => {
      if (called) return;
      called = true;
      clearTimeout(safety);
      this.track.removeEventListener('transitionend', onEnd);
      cb();
    };
    const onEnd = (e) => {
      if (e.target !== this.track) return; // ignore children
      done();
    };
    this.track.addEventListener('transitionend', onEnd);
    const safety = setTimeout(done, 700); // safety net
  }

  /* ---------- Navigation ---------- */
  next() {
    if (this.moving) return;

    if (!this.infinite) {
      if (this.index >= this.total - 1) return;
      this.moving = true;
      this.index++;
      this._setPosition(this.index, true);
      this._afterTransition(() => {
        this.moving = false;
        this._updateDots();
        this._playActiveVideo();
      });
      return;
    }

    // Infinite
    this.moving = true;
    if (this.index === this.total - 1) {
      // 1) animate into the clone of the first slide
      this._setPosition(this.total + this.cloneOffset, true);
      // 2) when finished, instantly snap to the real first slide
      this._afterTransition(() => {
        this.index = 0;
        this._setPosition(this.cloneOffset, false);
        this.moving = false;
        this._updateDots();
        this._playActiveVideo();
      });
    } else {
      this.index++;
      this._setPosition(this.index + this.cloneOffset, true);
      this._afterTransition(() => {
        this.moving = false;
        this._updateDots();
        this._playActiveVideo();
      });
    }
  }

  prev() {
    if (this.moving) return;

    if (!this.infinite) {
      if (this.index <= 0) return;
      this.moving = true;
      this.index--;
      this._setPosition(this.index, true);
      this._afterTransition(() => {
        this.moving = false;
        this._updateDots();
        this._playActiveVideo();
      });
      return;
    }

    // Infinite
    this.moving = true;
    if (this.index === 0) {
      // 1) animate into the clone of the last slide
      this._setPosition(0, true);
      // 2) when finished, instantly snap to the real last slide
      this._afterTransition(() => {
        this.index = this.total - 1;
        this._setPosition(this.index + this.cloneOffset, false);
        this.moving = false;
        this._updateDots();
        this._playActiveVideo();
      });
    } else {
      this.index--;
      this._setPosition(this.index + this.cloneOffset, true);
      this._afterTransition(() => {
        this.moving = false;
        this._updateDots();
        this._playActiveVideo();
      });
    }
  }

  goTo(n) {
    if (this.moving || n === this.index) return;
    this.moving = true;
    this.index = n;
    this._setPosition(this.infinite ? n + this.cloneOffset : n, true);
    this._afterTransition(() => {
      this.moving = false;
      this._updateDots();
      this._playActiveVideo();
    });
  }

  /* ---------- Autoplay ---------- */
  _play() {
    this._stop();
    this._playActiveVideo();
    this.timer = setInterval(() => {
      this.direction === 'backward' ? this.prev() : this.next();
    }, this.delay);
  }

  _stop() {
    clearInterval(this.timer);
    this.timer = null;
    this._pauseAllVideos();
  }

  _resetTimer() {
    if (!this.delay) return;
    this._stop();
    setTimeout(() => this._play(), this.delay);
  }

  /* ---------- Events ---------- */
  _bindEvents() {
    this.prevBtn?.addEventListener('click', () => { this.prev(); this._resetTimer(); });
    this.nextBtn?.addEventListener('click', () => { this.next(); this._resetTimer(); });

    this.dotsWrap?.addEventListener('click', (e) => {
      const dot = e.target.closest('.gemi-hero-slider__dot');
      if (!dot) return;
      this.goTo(parseInt(dot.dataset.slide, 10));
      this._resetTimer();
    });

    let startX = 0;
    this.track.addEventListener('touchstart', (e) => { startX = e.changedTouches[0].screenX; }, { passive: true });
    this.track.addEventListener('touchend', (e) => {
      const diff = startX - e.changedTouches[0].screenX;
      if (Math.abs(diff) < 40) return;
      diff > 0 ? this.next() : this.prev();
      this._resetTimer();
    }, { passive: true });

    this.el.setAttribute('tabindex', '0');
    this.el.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.key === 'ArrowLeft' ? this.prev() : this.next();
      this._resetTimer();
    });

    if (this.pauseHover) {
      this.el.addEventListener('mouseenter', () => this._stop());
      this.el.addEventListener('mouseleave', () => this._play());
    }

    document.addEventListener('visibilitychange', () => {
      document.hidden ? this._stop() : this._play();
    });
  }
}

/* Initialize */
(function init() {
  function boot() {
    document.querySelectorAll('.gemi-hero-slider').forEach(el => {
      if (el._slider) return;
      el._slider = new GemiHeroSlider(el);
    });
  }
  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);
})();